import { lazy, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/browser';
import { Provider } from 'react-redux';
import axios from 'axios';
import dayjs from 'dayjs';
import LocalizedFormat from 'dayjs/plugin/localizedFormat';
import 'dayjs/locale/fr';
import {
  BrowserRouter,
  Switch,
  Redirect as RedirectRouter,
  Route,
} from 'react-router-dom';
import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import { HelmetProvider } from 'react-helmet-async';
import '../style/libs/bootstrap.min.css';
import '../style/style.scss';
// clinic
import DashboardClinic from 'components/clinic/dashboard';
import ClinicOnboarding from 'components/clinic/onboarding';
import SignInClinic from 'components/clinic/signin';

// eslint-disable-next-line import/no-cycle
import AppLayout from './layouts/AppLayout';
import LDProvider from './LDProvider';
import LanguageProvider from './LanguageProvider';
import { ENVIRONMENT, API_URL } from './environment';
import { AUTH_USER } from './actions/types';
import AppRoute from './layouts/AppRoute';
import SessionLayout from './layouts/SessionLayout';
import NewVideoLayout from './layouts/NewVideoLayout';
import ScrollToTop from './utils/ScrollToTop';
import { useDefaultTimezone } from './utils/timezone';
import ModuleLoadingSpinner from './components/global/ModuleLoadingSpinner';
import AppHelmet from './components/global/AppHelmet';

// Authentication
import Redirect from './components/auth/Redirect';
import RedirectClientVideo from './components/auth/RedirectClientVideo';
import RequireAuth from './components/auth/RequireAuth';
import RequireAdmin from './components/auth/RequireAdmin';
import VerifyPin from './components/auth/TwoFactor/VerifyPin';
import Signin from './components/auth/SignIn';
import ForgotPassword from './components/auth/ForgotPassword';
import ForgotPasswordConfirmation from './components/auth/ForgotPasswordConfirmation';
import ResetPassword from './components/auth/ResetPassword';
import ConfirmationEmail from './components/auth/ConfirmationEmail';
import Confirmation from './components/auth/Confirmation';
// Onboarding
import PracticeOnboarding from './components/onboarding';
import GenericOnboarding from './components/onboarding_generic';

// Sessions
import SessionAuth from './components/sessions';
import WaitingRoom from './components/sessions/waiting_room';
import PostSessionAssessment from './components/sessions/postSessionAssessment/index';
import PractitionerVideo from './components/sessions/practitioner_video';
import ClientVideo from './components/sessions/client_video';
import NotFoundPage from './NotFoundPage';
import SharedFilesRoutes from './components/shared_files/SharedFilesRoutes';

import { store } from './utils/store';
import { inkTheme } from './utils/themeExports';

dayjs.extend(LocalizedFormat);
dayjs.locale(localStorage.getItem('locale') || 'en');

axios.interceptors.request.use(
  request => {
    if (request.url.includes(API_URL)) {
      const locale = localStorage.getItem('locale') || 'en';
      request.params = { ...request.params, locale };
    }
    return request;
  },
  error => Promise.reject(error),
);

// Use previously set timezone
useDefaultTimezone();

const token = localStorage.getItem('token');
if (token !== 'undefined' && token !== undefined && token !== null) {
  store.dispatch({ type: AUTH_USER });
}

if (ENV_SENTRY_DSN) {
  Sentry.init({
    dsn: ENV_SENTRY_DSN,
    environment: ENVIRONMENT,
    release: ENV_APP_VERSION,
    // Twilio Sync rate-limit noise; real fix is Conversations client reuse (POD2-2122).
    ignoreErrors: [/CPS_UPSTREAM_PER_CONNECTION/],
  });
}

// Renders the lazy component with its loading spinner in place, so the
// fallback appears inside the surrounding layout instead of replacing it.
const lazyLoad = importComponent => {
  const LazyComponent = lazy(importComponent);
  const LoadableComponent = props => (
    <Suspense fallback={<ModuleLoadingSpinner />}>
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <LazyComponent {...props} />
    </Suspense>
  );
  return LoadableComponent;
};

const Dashboard = lazyLoad(() =>
  import(/* webpackChunkName: "dashboard" */ 'components/dashboard/Dashboard'),
);

const Schedule = lazyLoad(() =>
  import(/* webpackChunkName: "schedule" */ 'components/schedule'),
);

const SupportRoutes = lazyLoad(() =>
  import(/* webpackChunkName: "support" */ 'components/support/SupportRoutes'),
);

const PreferencesRoutes = lazyLoad(() =>
  import(
    /* webpackChunkName: "preferences" */ 'components/preferences/PreferencesRoutes'
  ),
);

const Billing = lazyLoad(() =>
  import(/* webpackChunkName: "billing" */ 'components/billing'),
);

const SessionNotesRoutes = lazyLoad(() =>
  import(
    /* webpackChunkName: "process" */ 'components/session_notes/SessionNotesRoutes'
  ),
);

const ClientsRoutes = lazyLoad(() =>
  import(/* webpackChunkName: "clients" */ 'components/clients/ClientsRoutes'),
);

const MessagingRoutes = lazyLoad(() =>
  import(
    /* webpackChunkName: "dashboard" */ 'components/messaging/MessagingRoutes'
  ),
);

const AdminRoutes = lazyLoad(() =>
  import(/* webpackChunkName: "admin" */ 'components/admin/AdminRoutes'),
);

const PayoutsRoutes = lazyLoad(() =>
  import(/* webpackChunkName: "payouts" */ 'components/payouts/PayoutsRoutes'),
);

const root = createRoot(document.querySelector('#root'));
root.render(
  <HelmetProvider>
    <Provider store={store}>
      <LanguageProvider>
        <LDProvider>
          <StyledEngineProvider injectFirst>
            <ThemeProvider theme={inkTheme}>
              <BrowserRouter>
                <ScrollToTop>
                  <AppHelmet />
                  <Switch>
                    <AppRoute
                      layout={AppLayout}
                      path="/dashboard"
                      component={RequireAuth(Dashboard)}
                    />
                    <AppRoute
                      layout={AppLayout}
                      path="/schedule"
                      component={RequireAuth(Schedule)}
                    />
                    <AppRoute
                      layout={AppLayout}
                      path="/clients"
                      component={RequireAuth(ClientsRoutes)}
                    />
                    <AppRoute
                      layout={AppLayout}
                      path="/session_notes"
                      component={RequireAuth(SessionNotesRoutes)}
                    />
                    <AppRoute
                      layout={AppLayout}
                      path="/shared_files"
                      component={RequireAuth(SharedFilesRoutes)}
                    />
                    <AppRoute
                      layout={AppLayout}
                      path="/billing"
                      component={RequireAuth(Billing)}
                    />
                    <AppRoute
                      layout={AppLayout}
                      path="/messages"
                      component={RequireAuth(MessagingRoutes)}
                    />
                    <AppRoute
                      layout={AppLayout}
                      path="/preferences"
                      component={RequireAuth(PreferencesRoutes)}
                    />
                    <AppRoute
                      layout={AppLayout}
                      path="/support"
                      component={RequireAuth(SupportRoutes)}
                    />
                    <AppRoute
                      layout={AppLayout}
                      path="/admin"
                      component={RequireAuth(RequireAdmin(AdminRoutes))}
                    />
                    <AppRoute
                      layout={AppLayout}
                      path="/forgot_password"
                      component={ForgotPassword}
                    />
                    <AppRoute
                      layout={AppLayout}
                      path="/forgot_password_confirmation"
                      component={ForgotPasswordConfirmation}
                    />
                    <AppRoute
                      layout={AppLayout}
                      path="/redirect/:token"
                      component={Redirect}
                    />
                    <AppRoute
                      layout={AppLayout}
                      path="/reset_password/:token"
                      component={ResetPassword}
                    />
                    <AppRoute
                      layout={AppLayout}
                      path="/confirmation/:token"
                      component={Confirmation}
                    />
                    <AppRoute
                      layout={AppLayout}
                      path="/confirmation"
                      component={ConfirmationEmail}
                    />
                    <AppRoute
                      layout={AppLayout}
                      path="/signin"
                      component={Signin}
                    />

                    <AppRoute
                      layout={AppLayout}
                      path="/two_factor"
                      component={VerifyPin}
                    />
                    <AppRoute
                      layout={AppLayout}
                      path="/signup"
                      component={PracticeOnboarding}
                    />
                    <AppRoute
                      layout={AppLayout}
                      path="/signup_specialized"
                      component={GenericOnboarding}
                    />
                    <AppRoute
                      layout={SessionLayout}
                      exact
                      path="/sessions/:id/waiting_room/:token?"
                      component={SessionAuth(WaitingRoom)}
                    />
                    <AppRoute
                      layout={SessionLayout}
                      path="/sessions/:id/post_session_assessments"
                      component={RequireAuth(PostSessionAssessment)}
                    />
                    <AppRoute
                      layout={NewVideoLayout}
                      path="/sessions/clients/video_call/:id"
                      component={RequireAuth(ClientVideo)}
                    />
                    <AppRoute
                      layout={props => <div>{props.children}</div>}
                      path="/sessions/clients/redirect/:appointment_id/:token"
                      component={RedirectClientVideo}
                    />

                    <AppRoute
                      layout={SessionLayout}
                      path="/sessions/providers/video_call/:id"
                      component={RequireAuth(PractitionerVideo)}
                    />
                    <AppRoute
                      layout={AppLayout}
                      path="/payouts"
                      component={RequireAuth(PayoutsRoutes)}
                    />
                    <AppRoute
                      layout={AppLayout}
                      exact
                      path="/"
                      component={RequireAuth(Dashboard)}
                    />
                    <AppRoute
                      layout={props => <div>{props.children}</div>}
                      path="/404"
                      component={NotFoundPage}
                    />
                    {/* clinic routes - refactor might needed */}
                    <Route path="/signup_clinic" component={ClinicOnboarding} />
                    <Route path="/dashboard_clinic" component={DashboardClinic} />
                    <Route path="/signin_clinic" component={SignInClinic} />
                    <RedirectRouter to="/404" />
                  </Switch>
                </ScrollToTop>
              </BrowserRouter>
            </ThemeProvider>
          </StyledEngineProvider>
        </LDProvider>
      </LanguageProvider>
    </Provider>
  </HelmetProvider>,
);
